import { EVerticalNames, EOnboaringFormStep, ESubVerticalNames, ERegion } from '@constants';
import { Form } from 'antd';
import { CarouselRef } from 'antd/es/carousel';
import { useMemo } from 'react';
import { create } from 'zustand';

import { useUserVertical } from './useUserVertical';

import { getOnboardingSteps } from '@/pages/Student/Onboarding/config/getSteps';
import { useOnboardingForm } from '@/providers';
import { useMutatePlanification, useMutateMySubscriptions, useProfile } from '@/store';

type IStore = {
  stepIndex: number;
  isOnboardingStarted: boolean;
  carouselRef: React.RefObject<CarouselRef>;
  examGroupDate: Date | undefined;
  loadingAfterContract: boolean;
  setExamGroupDate: (examGroupDate: Date | undefined) => void;
  setStepIndex: (stepIndex: number) => void;
  startOnboarding: () => void;
  startLoadingAfterContract: () => void;
};

const store = create<IStore>(set => ({
  stepIndex: 0,
  isOnboardingStarted: false,
  carouselRef: { current: null },
  examGroupDate: undefined,
  loadingAfterContract: false,
  setExamGroupDate: examGroupDate => set({ examGroupDate }),
  setStepIndex: stepIndex => set({ stepIndex }),
  startOnboarding: () => {
    set({ isOnboardingStarted: true });
  },
  startLoadingAfterContract: () => {
    set({ loadingAfterContract: true });
  }
}));

export const useOnboardingFormLogic = () => {
  const {
    stepIndex,
    setStepIndex,
    isOnboardingStarted,
    startOnboarding,
    carouselRef,
    examGroupDate,
    setExamGroupDate,
    startLoadingAfterContract,
    loadingAfterContract
  } = store();
  const { setMyVisibleSubscriptions } = useMutateMySubscriptions();
  const { profile } = useProfile();
  const { userVertical, userSubVertical } = useUserVertical(profile);
  const { createPlanification, isWorking: isPlanificationLoading } = useMutatePlanification();
  const form = useOnboardingForm();
  const planification = Form.useWatch(['planification'], form);

  const isUserFromFP = userVertical === EVerticalNames.FP;
  const isUserFromPCE =
    userVertical === EVerticalNames.UNI && userSubVertical === ESubVerticalNames.PCE;
  const isUserFromUni = userVertical === EVerticalNames.UNI && !isUserFromPCE;
  const isUserEdu =
    userVertical === EVerticalNames.OPOS && userSubVertical === ESubVerticalNames.EDU;
  const isUserOpos = userVertical === EVerticalNames.OPOS && !isUserEdu;

  const hasUserVerticalAndCurrentStepIsStudyPlan = () => {
    return (
      (isUserFromFP || isUserOpos || isUserEdu) && currentStep === EOnboaringFormStep.STUDY_PLAN
    );
  };

  const isUserFromUniOrPceAndCrurrentStepIsChooseCourse = () => {
    return (isUserFromUni || isUserFromPCE) && currentStep === EOnboaringFormStep.CHOOSE_COURSES;
  };

  const handleNextStep = () => {
    if (!carouselRef.current || !profile) return;
    if (
      hasUserVerticalAndCurrentStepIsStudyPlan() ||
      isUserFromUniOrPceAndCrurrentStepIsChooseCourse()
    ) {
      createPlanification(
        {
          ...planification,
          userId: profile._id,
          includeAllActivities: true,
          endDate: planification?.endDate ? new Date(planification?.endDate) : undefined,
          restDates: (planification?.restDates ?? []).map((date: Date) => new Date(date)),
          restWeekdays: planification?.restWeekdays ?? []
        },
        {
          onSuccess: async () => {
            const selected = planification?.subscriptions;
            if (Array.isArray(selected) && selected.length > 0) {
              await setMyVisibleSubscriptions(selected);
            }
            setStepIndex(stepIndex + 1);
            carouselRef.current?.next();
          }
        }
      );
      return;
    }
    if (currentStep === EOnboaringFormStep.CHOSEN_REASONS) {
      form.submit();
    }
    setStepIndex(stepIndex + 1);
    carouselRef.current.next();
  };

  const handlePreviousStep = () => {
    if (!carouselRef.current) return;
    setStepIndex(stepIndex - 1);
    carouselRef.current.prev();
  };

  const selectedSteps = useMemo(() => {
    return getOnboardingSteps({
      vertical: userVertical,
      subVertical: userSubVertical,
      region: profile?.regionConfig ?? ERegion.ES
    });
  }, [userVertical, userSubVertical, profile?.regionConfig]);

  const currentStep = selectedSteps[stepIndex];
  const stepLength = selectedSteps.length;

  return {
    form,
    carouselRef,
    currentStep,
    handleNextStep,
    isOnboardingStarted,
    selectedSteps,
    startOnboarding,
    setStepIndex,
    handlePreviousStep,
    stepLength,
    stepIndex,
    examGroupDate,
    setExamGroupDate,
    startLoadingAfterContract,
    loadingAfterContract,
    isNextStepLoading: isPlanificationLoading,
    isOnboardingFinished: currentStep === EOnboaringFormStep.FINAL_STEP,
    userInfo: {
      isUserFromFP,
      isUserFromUni,
      isUserFromPCE,
      isUserOpos,
      isUserEdu
    }
  };
};
